import { Map } from "immutable";

import { Action } from "common/types/redux";

import makeAssignmentsReducer from "seneca-common/features/assignments/utils/reducers/makeAssignmentsReducer";

import actionTypes from "../../../action-types";
import {
  receiveAssignment as receiveAssignmentAction,
  UpdatedMarks
} from "../../../actions";
import AssigneeQuestionStat, {
  EmbeddedAnswerMark
} from "../../../models/Assignee/QuestionStat";
import TeacherAssignment from "../../../models/TeacherAssignment";
import assignmentReducer from "./assignment";

const baseAssignmentsReducer = makeAssignmentsReducer({
  assignmentReducer,
  receiveAssignmentActionCreator: receiveAssignmentAction,
  receiveAssignmentActionType: actionTypes.RECEIVE_ASSIGNMENT,
  receiveAssignmentsActionType: actionTypes.RECEIVE_ASSIGNMENTS,
  deleteAssignmentsActionType: actionTypes.DELETE_ASSIGNMENTS
});

export default function teacherAssignmentsReducer(
  assignments: Map<string, TeacherAssignment> = Map<
    string,
    TeacherAssignment
  >(),
  action: Action<any>
) {
  let newAssignments = baseAssignmentsReducer(assignments, action);

  // handle teacher specific actions here

  switch (action.type) {
    case actionTypes.DELETE_ASSIGNMENT: {
      return newAssignments.delete(action.payload);
    }

    case actionTypes.FETCHED_ASSIGNMENT_QUESTION_MARKS: {
      const updatedMarks: UpdatedMarks[] = action.payload;
      return (newAssignments as Map<string, TeacherAssignment>).withMutations(
        mutableState => {
          updatedMarks.forEach(
            ({
              assignmentId,
              assigneeId,
              questionId,
              teacherMark,
              embeddedAnswerMarks
            }) => {
              mutableState.setIn(
                [
                  assignmentId,
                  "assignees",
                  assigneeId,
                  "questionStats",
                  questionId,
                  "teacherMark"
                ],
                teacherMark
              );
              if (embeddedAnswerMarks) {
                mutableState.setIn(
                  [
                    assignmentId,
                    "assignees",
                    assigneeId,
                    "questionStats",
                    questionId,
                    "embeddedAnswerMarks"
                  ],
                  embeddedAnswerMarks
                );
              }
            }
          );
        }
      );
    }

    case actionTypes.UPDATE_ASSIGNMENT_QUESTION_MARKS: {
      const { assignmentId, assigneeId, questionId, teacherMark } =
        action.payload;
      return newAssignments.updateIn(
        [assignmentId, "assignees", assigneeId, "questionStats", questionId],
        (stat: AssigneeQuestionStat) =>
          new AssigneeQuestionStat({
            ...stat.toObject(),
            teacherMark
          })
      );
    }

    case actionTypes.UPDATE_ASSIGNMENT_EMBEDDED_QUESTION_MARK: {
      const {
        assignmentId,
        assigneeId,
        questionId,
        embeddedAnswerMarks,
        allMarked
      } = action.payload;

      const teacherMark = allMarked
        ? embeddedAnswerMarks.reduce(
            (prev: number, curr: EmbeddedAnswerMark) =>
              prev + (curr.teacherMark || 0),
            0
          )
        : undefined;

      return newAssignments.updateIn(
        [assignmentId, "assignees", assigneeId, "questionStats", questionId],
        (stat: AssigneeQuestionStat) =>
          new AssigneeQuestionStat({
            ...stat.toObject(),
            teacherMark,
            embeddedAnswerMarks
          })
      );
    }

    case actionTypes.UPDATE_ASSIGNMENT_QUESTIONS_AGGREGATES: {
      const {
        assignmentId,
        assigneeId,
        assignmentAverageMark,
        sumQuestionMarks
      } = action.payload;

      newAssignments = newAssignments.setIn(
        [assignmentId, "questionAggregations", "averageMark"],
        assignmentAverageMark
      );

      newAssignments = newAssignments.setIn(
        [
          assignmentId,
          "assignees",
          assigneeId,
          "aggregatedStats",
          "sumQuestionMarks"
        ],
        sumQuestionMarks
      );

      return newAssignments;
    }

    default:
      return newAssignments;
  }
}
